import React, { useEffect, useState } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Search, 
  Filter,
  Download,
  Save,
  X,
  Tag,
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle
} from 'lucide-react';
import { promotionsAPI } from '../services/api';
import {
  ModernCard,
  ModernButton,
  ModernInput,
  ModernSelect,
  ModernTable,
  ModernBadge,
  ModernModal,
  ModernSearchBar,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';
import toast from 'react-hot-toast';

const defaultForm = {
  name: '',
  description: '',
  start_date: '',
  end_date: '',
};

const Promotions = () => {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [editingId, setEditingId] = useState(null);
  const [submitLoading, setSubmitLoading] = useState(false);

  // Fetch promotions
  const fetchPromotions = async () => {
    setLoading(true);
    try {
      const res = await promotionsAPI.getPromotions();
      setPromotions(res.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des promotions');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchPromotions();
  }, []);

  // Handle form input
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  // Handle add/edit submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);
    try {
      if (editingId) {
        await promotionsAPI.updatePromotion(editingId, form);
        toast.success('Promotion modifiée');
      } else {
        await promotionsAPI.createPromotion(form);
        toast.success('Promotion ajoutée');
      }
      setShowForm(false);
      setForm(defaultForm);
      setEditingId(null);
      fetchPromotions();
    } catch (err) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Handle edit
  const handleEdit = (promotion) => {
    setForm({
      name: promotion.name,
      description: promotion.description || '',
      start_date: promotion.start_date ? promotion.start_date.slice(0, 10) : '',
      end_date: promotion.end_date ? promotion.end_date.slice(0, 10) : '',
    });
    setEditingId(promotion.id);
    setShowForm(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette promotion ?')) return;
    try {
      await promotionsAPI.deletePromotion(id);
      toast.success('Promotion supprimée');
      fetchPromotions();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Handle add new
  const handleAdd = () => {
    setForm(defaultForm);
    setEditingId(null);
    setShowForm(true);
  };

  // Get promotion status badge
  const getPromotionStatusBadge = (startDate, endDate) => {
    const now = new Date();
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;
    
    if (!start && !end) return <ModernBadge variant="success">Active</ModernBadge>;
    if (start && now < start) return <ModernBadge variant="primary">À venir</ModernBadge>;
    if (end && now > end) return <ModernBadge variant="danger">Expirée</ModernBadge>;
    return <ModernBadge variant="success">Active</ModernBadge>;
  };

  const filteredPromotions = promotions.filter(promotion => {
    return String(promotion.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
           String(promotion.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
           String(promotion.start_date || '').includes(searchTerm) ||
           String(promotion.end_date || '').includes(searchTerm);
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const tableColumns = [
    {
      key: 'name',
      label: 'Nom',
      render: (value, promotion) => (
        <div className="flex items-center">
          <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
            <Tag className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <div className="font-semibold text-neutral-900">{value}</div>
          </div>
        </div>
      )
    },
    {
      key: 'description',
      label: 'Description',
      render: (value) => (
        <div className="max-w-xs">
          <p className="text-sm text-neutral-700 truncate">{value || '-'}</p>
        </div>
      )
    },
    {
      key: 'start_date',
      label: 'Date de début',
      render: (value) => (
        <div className="flex items-center">
          <Calendar className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm">{formatDate(value)}</span>
        </div>
      )
    },
    {
      key: 'end_date',
      label: 'Date de fin',
      render: (value) => (
        <div className="flex items-center">
          <Calendar className="w-4 h-4 text-neutral-400 mr-2" />
          <span className="text-sm">{formatDate(value)}</span>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Statut',
      render: (value, promotion) => getPromotionStatusBadge(promotion.start_date, promotion.end_date)
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (value, promotion) => (
        <div className="flex items-center space-x-2">
          <ModernButton
            variant="ghost"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => handleEdit(promotion)}
          >
            Voir
          </ModernButton>
          <ModernButton
            variant="primary"
            size="sm"
            icon={<Edit className="w-4 h-4" />}
            onClick={() => handleEdit(promotion)}
          >
            Modifier
          </ModernButton>
          <ModernButton
            variant="danger"
            size="sm"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={() => handleDelete(promotion.id)}
          >
            Supprimer
          </ModernButton>
        </div>
      )
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <ModernLoadingSpinner size="lg" />
      </div>
    );
  }

  const activePromotions = promotions.filter(p => {
    const now = new Date();
    const start = p.start_date ? new Date(p.start_date) : null;
    const end = p.end_date ? new Date(p.end_date) : null;
    return (!start || now >= start) && (!end || now <= end);
  }).length;

  const upcomingPromotions = promotions.filter(p => {
    const now = new Date();
    const start = p.start_date ? new Date(p.start_date) : null;
    return start && now < start;
  }).length;

  const expiredPromotions = promotions.filter(p => {
    const now = new Date();
    const end = p.end_date ? new Date(p.end_date) : null;
    return end && now > end;
  }).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">Promotions</h1>
          <p className="text-neutral-600">Gérez les promotions et offres spéciales</p>
        </div>
        <div className="flex items-center space-x-3">
          <ModernSearchBar
            placeholder="Rechercher des promotions..."
            onSearch={setSearchTerm}
            className="w-64"
            debounce={300}
          />
          <ModernButton
            variant="primary"
            icon={<Plus className="w-4 h-4" />}
            onClick={handleAdd}
          >
            Ajouter une promotion
          </ModernButton>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Total Promotions</p>
              <p className="text-2xl font-bold text-neutral-900">{promotions.length}</p>
            </div>
            <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
              <Tag className="w-6 h-6 text-primary-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Actives</p>
              <p className="text-2xl font-bold text-success-600">{activePromotions}</p>
            </div>
            <div className="w-12 h-12 bg-success-100 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-success-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">À venir</p>
              <p className="text-2xl font-bold text-info-600">{upcomingPromotions}</p>
            </div>
            <div className="w-12 h-12 bg-info-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-info-600" />
            </div>
          </div>
        </ModernCard>

        <ModernCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600">Expirées</p>
              <p className="text-2xl font-bold text-danger-600">{expiredPromotions}</p>
            </div>
            <div className="w-12 h-12 bg-danger-100 rounded-lg flex items-center justify-center">
              <XCircle className="w-6 h-6 text-danger-600" />
            </div>
          </div>
        </ModernCard>
      </div>

      {/* Promotions Table */}
      <ModernCard className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">Liste des promotions</h3>
            <p className="text-sm text-neutral-600">{filteredPromotions.length} promotions trouvées</p>
          </div>
          <div className="flex items-center space-x-2">
            <ModernButton variant="ghost" size="sm" icon={<Filter className="w-4 h-4" />}>
              Filtrer
            </ModernButton>
            <ModernButton variant="ghost" size="sm" icon={<Download className="w-4 h-4" />}>
              Exporter
            </ModernButton>
          </div>
        </div>

        <ModernTable
          columns={tableColumns}
          data={filteredPromotions}
          className="w-full"
        />
      </ModernCard>

      {/* Add/Edit Modal */}
      <ModernModal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title={editingId ? 'Modifier la promotion' : 'Ajouter une promotion'}
        size="lg"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <ModernInput
              label="Nom"
              name="name"
              value={form.name}
              onChange={handleChange}
              placeholder="Nom de la promotion"
              icon={<Tag className="w-5 h-5" />}
              required
            />

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Description
              </label>
              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                rows="3"
                placeholder="Description de la promotion..."
              />
            </div>

            <ModernInput
              label="Date de début"
              name="start_date"
              type="date"
              value={form.start_date}
              onChange={handleChange}
              icon={<Calendar className="w-5 h-5" />}
            />

            <ModernInput
              label="Date de fin"
              name="end_date"
              type="date"
              value={form.end_date}
              onChange={handleChange}
              icon={<Calendar className="w-5 h-5" />}
            />
          </div>

          <div className="flex items-center justify-end space-x-3 pt-4">
            <ModernButton
              type="button"
              variant="secondary"
              onClick={() => setShowForm(false)}
            >
              Annuler
            </ModernButton>
            <ModernButton
              type="submit"
              variant="primary"
              icon={<Save className="w-4 h-4" />}
              disabled={submitLoading}
            >
              {submitLoading ? 'Enregistrement...' : (editingId ? 'Modifier' : 'Ajouter')}
            </ModernButton>
          </div>
        </form>
      </ModernModal>
    </div>
  );
};

export default Promotions; 